import { useEffect, useRef } from 'react';
import { FitAddon } from '@xterm/addon-fit';
import { Terminal } from '@xterm/xterm';
import '@xterm/xterm/css/xterm.css';

import { registerLinkProvider } from '@/utils/terminal-link-provider';
import { WebSocketConnection, wsUrl } from '@/utils/websocket';

const THEME = {
  background: '#0c0e16',
  foreground: '#e5e7eb',
  cursor: '#e5e7eb',
  selectionBackground: '#334155',
};

interface TerminalSurfaceProps {
  base: string;
  /** Reconnect to this PTY session if provided; otherwise a fresh one is created. */
  ptySessionId: string | null;
  /** Called once the server assigns/confirms the PTY session id, so the tab can persist it. */
  onSessionId: (id: string) => void;
  active: boolean;
}

export function TerminalSurface({ base, ptySessionId, onSessionId, active }: TerminalSurfaceProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const connRef = useRef<WebSocketConnection | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;
    let teardown: (() => void) | null = null;

    const isVisible = () => container.offsetParent !== null && container.clientWidth > 0 && container.clientHeight > 0;

    // Defer creating the terminal + PTY connection until the container is
    // actually visible and sized. Otherwise the initial `fit()` runs against a
    // 0×0 element (e.g. the auto-opened first tab while the panel shows Files),
    // the shell starts at a tiny width, and its first prompt prints mangled.
    const start = () => {
      if (disposed || teardown) return;

      const term = new Terminal({
        fontFamily: '"Geist Mono Variable", ui-monospace, monospace',
        fontSize: 12.5,
        theme: THEME,
        allowProposedApi: true,
      });
      const fitAddon = new FitAddon();
      term.loadAddon(fitAddon);
      term.open(container);
      registerLinkProvider(term);
      fitAddon.fit();
      term.focus();
      termRef.current = term;
      fitAddonRef.current = fitAddon;

      const query = ptySessionId ? `?session_id=${encodeURIComponent(ptySessionId)}` : '';
      const conn = new WebSocketConnection({
        url: () => wsUrl(`${base}/terminal${query}`),
        binaryType: 'arraybuffer',
        onMessage: (event) => {
          if (typeof event.data === 'string') {
            try {
              const ctrl = JSON.parse(event.data) as { type?: string; ptySessionId?: string };
              if (ctrl.type === 'control' && ctrl.ptySessionId) {
                onSessionId(ctrl.ptySessionId);
              }
            } catch {
              // not a control frame
            }
            return;
          }
          term.write(new Uint8Array(event.data as ArrayBuffer));
        },
      });
      connRef.current = conn;

      const dataDisposable = term.onData((data) => conn.send(data));

      // Dragging the panel divider fires this every frame, so coalesce to one
      // fit per paint and only tell the PTY when the cell count actually moved —
      // a few pixels of drag usually doesn't cross a cell boundary. This also
      // makes hiding and re-showing the panel free: the box comes back the size
      // it left, so the fit is a no-op and nothing goes over the wire.
      let sentCols = term.cols;
      let sentRows = term.rows;
      const fitAndSync = () => {
        fitAddon.fit();
        if (term.cols === sentCols && term.rows === sentRows) return;
        sentCols = term.cols;
        sentRows = term.rows;
        conn.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
      };

      let pending = 0;
      const resizeObserver = new ResizeObserver(() => {
        if (pending) return;
        pending = requestAnimationFrame(() => {
          pending = 0;
          if (!isVisible()) return;
          fitAndSync();
        });
      });
      resizeObserver.observe(container);

      // Geist Mono is self-hosted with `font-display: swap`, so the fit above can
      // measure the ui-monospace fallback and then have the real font swap in
      // underneath it — leaving the grid misaligned and the PTY on the wrong `cols`
      // until some later resize happens to correct it. Re-measure once the font is
      // in, going through fitAndSync so the PTY actually hears about it.
      void document.fonts.ready.then(() => {
        if (disposed) return;
        term.clearTextureAtlas();
        fitAndSync();
      });

      teardown = () => {
        if (pending) cancelAnimationFrame(pending);
        resizeObserver.disconnect();
        dataDisposable.dispose();
        conn.dispose();
        term.dispose();
        termRef.current = null;
        fitAddonRef.current = null;
        connRef.current = null;
      };
    };

    let gateObserver: ResizeObserver | null = null;
    if (isVisible()) {
      start();
    } else {
      gateObserver = new ResizeObserver(() => {
        if (!isVisible()) return;
        gateObserver?.disconnect();
        gateObserver = null;
        start();
      });
      gateObserver.observe(container);
    }

    return () => {
      disposed = true;
      gateObserver?.disconnect();
      teardown?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ptySessionId intentionally only seeds the initial connection; onSessionId is a stable callback
  }, [base]);

  useEffect(() => {
    if (!active) return;
    const el = containerRef.current;
    const term = termRef.current;
    const fitAddon = fitAddonRef.current;
    const conn = connRef.current;
    if (!el || !term || !fitAddon || !conn) return;
    if (el.offsetParent === null || el.clientWidth === 0 || el.clientHeight === 0) return;
    fitAddon.fit();
    conn.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
    term.focus();
  }, [active]);

  return (
    <div
      ref={containerRef}
      className="terminal-instance size-full"
      style={{ display: active ? 'block' : 'none' }}
      onMouseDown={() => termRef.current?.focus()}
    />
  );
}
